import { useCallback, useEffect, useMemo, useRef, type RefObject } from 'react';
import type Konva from 'konva';
import { toast } from 'sonner';
import type { CommandManager } from '../../../core/commands/CommandManager';
import { CreateShapeCommand } from '../../../core/commands/CreateShapeCommand';
import { SetSelectionCommand } from '../../../core/commands/SetSelectionCommand';
import { getShapeManifest } from '../../../lib/diagram/registries';
import { useMediaUpload } from '../../../domains/media/hooks/useMediaUpload';
import { getNodeMutationManager } from '../../../core/commands/mutations/NodeMutationManager';
import {
  getMediaUploadFriendlyMessage,
  type MediaUploadTranslateFn,
} from '../../../domains/media/utils/mediaUploadFriendlyMessage';
import { Tracker } from '../../../domains/analytics/services/Tracker';
import { MEDIA } from '../../../domains/analytics/constants/MetricEvents';
import { useTranslation } from '../../../hooks/useTranslation';
import { useDiagramStore } from '../../../stores/diagramStore';
import { validateShapeLimitOrPromptUpgrade } from '../../../utils/shapeLimitUtils';
import { layoutImageDropTopLeftPositions } from '../utils/imageDropLayout';
import { MAX_CANVAS_DROP_PDF_FILES } from '../utils/canvasImageFiles';
import {
  removeStaleUploadingMediaNodes,
  useCanvasMediaInsertionInternals,
  type CanvasPoint,
} from './useCanvasMediaInsertionInternals';

const PDF_DROP_GRID_GAP = 16;
const MIN_FAILED_UPLOAD_PLACEHOLDER_MS = 500;

interface UseCanvasPdfInsertionInput {
  canWrite: boolean;
  diagramId: string;
  stageRef: RefObject<Konva.Stage | null>;
  commandManager: CommandManager;
}

export function useCanvasPdfInsertion(input: UseCanvasPdfInsertionInput) {
  const { canWrite, diagramId, stageRef, commandManager } = input;
  const { t } = useTranslation('diagram');
  const { uploadMedia } = useMediaUpload({ source: 'canvas.pdf', diagramId, mediaKind: 'pdf' });
  const isInitialLoading = useDiagramStore((state) => state.isInitialLoading);
  const didRunRefreshCleanup = useRef(false);

  const defaultDimensions = useMemo(() => {
    const manifest = getShapeManifest('generic', 'pdf');
    return manifest?.defaultDimensions || { width: 180, height: 96 };
  }, []);

  const buildUploadedPdfAttrs = useCallback((objectKey: string) => ({
    objectKey,
    uploadState: 'ready' as const,
    uploadErrorMessage: undefined,
  }), []);

  const {
    markPendingNodeUploaded,
    removePendingNodeOnFailure,
    waitForMinFailedPlaceholderVisibility,
    getViewportCenter: getViewportCenterFromStage,
  } = useCanvasMediaInsertionInternals({
    commandManager,
    diagramId,
    mediaKind: 'pdf',
    uploadedAttrs: buildUploadedPdfAttrs,
    minFailedPlaceholderMs: MIN_FAILED_UPLOAD_PLACEHOLDER_MS,
  });

  useEffect(() => {
    if (didRunRefreshCleanup.current) return;
    if (isInitialLoading) return;
    didRunRefreshCleanup.current = true;

    // Stale in-flight uploads without an object key cannot be resumed after refresh.
    const mutationManager = getNodeMutationManager();
    removeStaleUploadingMediaNodes({
      nodes: useDiagramStore.getState().nodes,
      mediaTypes: new Set(['pdf']),
      deleteNode: mutationManager.deleteNode.bind(mutationManager),
    });
  }, [isInitialLoading]);

  const insertDroppedPdfFiles = useCallback(
    async (
      files: File[],
      anchorCenter: CanvasPoint,
      notice?: { skippedOverMaxCount: number },
    ): Promise<boolean> => {
      if (notice?.skippedOverMaxCount && notice.skippedOverMaxCount > 0) {
        toast.message(
          t('mediaUpload.multiDropTruncated', {
            max: String(MAX_CANVAS_DROP_PDF_FILES),
          }),
        );
      }
      if (!canWrite || files.length === 0) return false;

      const store = useDiagramStore.getState();
      if (!validateShapeLimitOrPromptUpgrade(store, files.length)) {
        return false;
      }

      const positions = layoutImageDropTopLeftPositions({
        count: files.length,
        anchorCenter,
        cellWidth: defaultDimensions.width,
        cellHeight: defaultDimensions.height,
        gap: PDF_DROP_GRID_GAP,
      });

      Tracker.track(MEDIA.CANVAS_IMAGE_DROP, { count: files.length, mediaKind: 'pdf' });

      const previousSelection = new Set(store.selectedIds);
      const shapeIds = files.map(
        (_, i) => `pdf-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 11)}`,
      );

      const creates = files.map(
        (file, i) =>
          new CreateShapeCommand({
            id: shapeIds[i],
            type: 'pdf',
            category: 'media',
            x: positions[i].x,
            y: positions[i].y,
            width: defaultDimensions.width,
            height: defaultDimensions.height,
            attrs: {
              objectKey: '',
              name: file.name.replace(/\.pdf$/i, ''),
              description: '',
              uploadState: 'uploading',
              uploadErrorMessage: undefined,
            },
          }),
      );

      const selectionCmd = new SetSelectionCommand(new Set(shapeIds), previousSelection);
      await commandManager.executeBatch([...creates, selectionCmd], 'Drop PDFs');

      const results = await Promise.all(
        files.map(async (file, i) => {
          const uploadStartedAt = Date.now();
          try {
            const uploaded = await uploadMedia(file);
            markPendingNodeUploaded(shapeIds[i], uploaded.objectKey);
            return { ok: true as const };
          } catch (error) {
            const friendly = getMediaUploadFriendlyMessage(error, t as MediaUploadTranslateFn);
            await waitForMinFailedPlaceholderVisibility(uploadStartedAt);
            removePendingNodeOnFailure(shapeIds[i]);
            toast.error(friendly);
            return { ok: false as const };
          }
        }),
      );

      const successes = results.filter((result) => result.ok);
      return successes.length > 0;
    },
    [
      canWrite,
      commandManager,
      defaultDimensions.height,
      defaultDimensions.width,
      markPendingNodeUploaded,
      removePendingNodeOnFailure,
      t,
      uploadMedia,
      waitForMinFailedPlaceholderVisibility,
    ],
  );

  const getViewportCenter = useCallback((): CanvasPoint | null => {
    return getViewportCenterFromStage(stageRef);
  }, [getViewportCenterFromStage, stageRef]);

  return {
    defaultDimensions,
    insertDroppedPdfFiles,
    getViewportCenter,
  };
}
